import { FormikProvider, useFormik } from 'formik';
import { isEqual, pick } from 'lodash';
import * as yup from 'yup';
import { isIbanCountry } from '@/modules/common/utils/bank-details.ts';
import {
  IBAN_REGEX,
  SWIFT_BIC_REGEX,
  US_ROUTING_NUMBER_REGEX,
} from '@/modules/common/utils/regex.ts';
import { useUpdateEntityContactPaymentDetailsMutation } from '@/modules/investor-dashboard/queries/entity.ts';
import { Button, TextInputField } from 'ui';
import { EntityWithDocumentResponse } from 'api';

export type entityEditableData = Pick<
  EntityWithDocumentResponse,
  | 'bank_account_bsb'
  | 'bank_account_swift'
  | 'bank_account_iban'
  | 'bank_account_routing_number'
  | 'bank_account_name'
  | 'bank_account_number'
  | 'bank_name'
  | 'country'
>;

const validationSchema = yup.object({
  bank_name: yup.string().trim().required('This field is required'),
  country: yup.string().trim(),
  bank_account_bsb: yup
    .string()
    .trim()
    .when('country', ([country], schema) => {
      return country === 'AUS' ? schema.required('This field is required') : schema.optional();
    }),
  bank_account_swift: yup
    .string()
    .trim()
    .test(
      'is-valid-swift-bic',
      'Invalid SWIFT/BIC. Enter 8 or 11 characters. Example: DEUTDEFF',
      value => !value || value === 'Unknown' || SWIFT_BIC_REGEX.test(value),
    )
    .when('country', ([country], schema) => {
      return country !== 'AUS' ? schema.required('This field is required') : schema.optional();
    }),
  bank_account_iban: yup
    .string()
    .trim()
    .test(
      'is-valid-iban',
      'Invalid IBAN. Enter 15 to 34 characters. Example: DE89370400440532013000',
      value => !value || value === 'Unknown' || IBAN_REGEX.test(value),
    )
    .when('country', ([country], schema) => {
      return isIbanCountry(country) ? schema.required('This field is required') : schema.optional();
    }),
  bank_account_routing_number: yup
    .string()
    .trim()
    .test(
      'is-valid-us-routing-number',
      'Invalid routing number. Enter 9 digits. Example: 021000021',
      value => !value || value === 'Unknown' || US_ROUTING_NUMBER_REGEX.test(value),
    )
    .when('country', ([country], schema) => {
      return country === 'USA' ? schema.required('This field is required') : schema.optional();
    }),
  bank_account_name: yup.string().trim().required('This field is required'),
  bank_account_number: yup.string().trim().required('This field is required'),
});

export interface EditEntityPaymentTabProps {
  onSuccess?: () => void;
  entity: EntityWithDocumentResponse;
}

export const EditEntityPaymentTab = ({ onSuccess, entity }: EditEntityPaymentTabProps) => {
  const { mutateAsync, isLoading } = useUpdateEntityContactPaymentDetailsMutation({ onSuccess });

  const formik = useFormik<entityEditableData>({
    validationSchema,
    initialValues: pick(entity, [
      'bank_account_bsb',
      'bank_account_swift',
      'bank_account_iban',
      'bank_account_routing_number',
      'bank_account_name',
      'bank_account_number',
      'bank_name',
      'country',
    ]),
    onSubmit: async values => {
      await mutateAsync({ ...values, id: entity.id });
    },
  });

  const isChanged = !isEqual(formik.values, formik.initialValues);

  return (
    <FormikProvider value={formik}>
      <div className="py-4">
        <div className="flex flex-col gap-8">
          <TextInputField name="bank_name" label="Bank name" />
          {formik.values.country === 'AUS' ? (
            <TextInputField name="bank_account_bsb" label="BSB" placeholder="123-456" />
          ) : (
            <>
              {formik.values.country === 'USA' && (
                <TextInputField
                  name="bank_account_routing_number"
                  label="Routing number"
                  placeholder="021000021"
                  hintLabel="Enter the 9-digit ABA routing number for your US bank account."
                />
              )}
              {isIbanCountry(formik.values.country) && (
                <TextInputField
                  name="bank_account_iban"
                  label="IBAN"
                  placeholder="DE89370400440532013000"
                  hintLabel="Enter your International Bank Account Number without spaces."
                />
              )}
              <TextInputField
                name="bank_account_swift"
                label="SWIFT/BIC"
                placeholder="DEUTDEFF"
                hintLabel="Enter your bank's 8 or 11 character SWIFT/BIC code."
              />
            </>
          )}
          <TextInputField name="bank_account_number" label="Account number" />
          <TextInputField name="bank_account_name" label="Account name" />
        </div>
      </div>
      <Button
        loading={isLoading}
        disabled={
          !isChanged || isLoading || Object.values(formik.errors).filter(item => !!item).length > 0
        }
        onClick={formik.handleSubmit}
        type="submit"
      >
        Save changes
      </Button>
    </FormikProvider>
  );
};
